import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PanelFrame } from '../workspace/PanelFrame';
import PublishEverywhereButton from '../PublishEverywhereButton';   // Feb-2026 cross-post
import { PenLine, Loader2, Sparkles, History, ScanSearch, Map, ChevronUp } from 'lucide-react';
import { usePW } from '@/lib/store';
import { useAuth } from '@/lib/auth_context';
import { useDemo } from '@/lib/demo_context';
import axios from 'axios';

import { SmartModelHint } from './editor/SmartModelHint';
import ManuscriptBody from './editor/ManuscriptBody';
import { ManuscriptToolboxDrawer } from './editor/ManuscriptToolboxDrawer';   // Feb-2026
import { WordCountPanel }          from './editor/WordCountPanel';             // Word Count Adjust
import EditorHeaderActions from './editor/EditorHeaderActions';
import EditorDrawers from './editor/EditorDrawers';
import InlineSuggestionPopover from './editor/InlineSuggestionPopover';
import LiveCoachPopover from '../coach/LiveCoachPopover';   // iter-training-v-ach-coach
import CoachHistoryDialog from '../coach/CoachHistoryDialog';   // iter-p2
import CoachHintDialog from '../coach/CoachHintDialog';         // iter-coach-actions
import useLiveCoach from './editor/useLiveCoach';            // iter-training-v-ach-coach

import { useChapterSuggestions }   from './editor/useChapterSuggestions';
import { useManuscriptSettings }   from './editor/useManuscriptSettings';
import { useEditorDrawers }        from './editor/useEditorDrawers';
import { useSpellCheck }           from './editor/useSpellCheck';
import { useEditorAiActions }      from './editor/useEditorAiActions';
import { useChapterMutations }     from './editor/useChapterMutations';
import { useSaveState }            from './editor/useSaveState';
import { SaveAsDialog }            from './editor/SaveAsDialog';
import { useEditorCapabilities }   from './editor/useEditorCapabilities';
import { useTokenCostHeuristics }  from './editor/useTokenCostHeuristics';
import { useEditorShortcuts }      from './editor/useEditorShortcuts';
import DraftNovelReadinessDialog   from '../dialogs/DraftNovelReadinessDialog';
import AiHandledBanner             from '@/components/AiHandledBanner';

// Inline wrapper so we only add the banner inside the editor layout
function _EditorAiBanner() {
  return (
    <div className="px-3 pb-1">
      <AiHandledBanner panelKey="editor" />
    </div>
  );
}
import SynopsisDialog              from './editor/SynopsisDialog';   // Feb-2026
import PlotBuilderDialog           from './editor/PlotBuilderDialog'; // Plot Builder (Pro/Studio)
import { RewriteDialog }           from '../dialogs/RewriteDialog';  // AI Rewrite Dialogue
import { bonusAddonsApi, chaptersApi } from '@/lib/api';                   // Continuity audit add-on
import useCostPreflight            from '@/lib/useCostPreflight';   // Feb-2026 · price approval gate
import ChapterGradePill            from './editor/ChapterGradePill';
import { consumePendingSynopsisOpen } from '@/lib/pendingIntents';   // Feb-2026
import { TokenNudgeBanner }          from './editor/TokenNudgeBanner';  // Feb-2026 token nudge
import { AiThinkingBanner }         from './editor/AiThinkingBanner';  // AI-busy indicator

// ── Creation Journey pill — read from localStorage, kept in sync via event ──
const CJ_STEP_IDS = ['synopsis', 'outline', 'write', 'chat', 'bible'];
function readJourneyState() {
  try {
    const dismissed = !!localStorage.getItem('loom:cj-dismissed');
    const minimised = localStorage.getItem('loom:cj-minimised') === '1';
    const doneCounts = CJ_STEP_IDS.filter((id) => !!localStorage.getItem(`loom:cj-step-${id}-done`)).length;
    const allDone = doneCounts === CJ_STEP_IDS.length;
    return { visible: !dismissed && minimised && !allDone, count: doneCounts, total: CJ_STEP_IDS.length };
  } catch { return { visible: false, count: 0, total: 5 }; }
}

// Placeholder used when a brand-new chapter has no content yet.
// iter55g25 — Blanked out to avoid confusing writers about who "Halden"
// and "Lyra" were. The blank-canvas UI renders regardless.
const SEED_FALLBACK = [''];

export function EditorPanel({ onClose, simpleMode }) {
  const { t } = useTranslation();
  const {
    chapters, activeChapterId, setActiveChapterId, project, generateChapter,
    streamingChapterId, streamingText, loadProject, tokens, activeMode,
    setActiveMode, setChapters, refreshTokens,
  } = usePW();
  const { user } = useAuth();
  const { isDemo, recordLock } = useDemo();

  // ------ capability gating ------------------------------------------------
  const {
    upgradeCta,
    canAiWriteChapters,
    canBulkDraft,
    canContinuityAudit,
    canScriptConvert,
    canExpandWordCount,
    guardedGenerateChapter,
    draftNovelOpen,
    openDraftNovel,
    closeDraftNovel,
  } = useEditorCapabilities({ generateChapter });

  // Default-deny while permissions are still loading.
  const canGenerate = Boolean(user?.permissions?.generate_ai);
  const canMutate   = Boolean(user?.permissions?.edit_chapters);
  const genTitle = isDemo
    ? t('editor.genTitleDemo')
    : (canGenerate ? '' : t('editor.genTitleNoPermission'));

  // ------ active chapter --------------------------------------------------
  const fallbackIdx = Math.min(5, chapters.length - 1);
  const ch  = chapters.find((c) => c.id === activeChapterId) || chapters[fallbackIdx] || chapters[0];
  const idx = ch ? chapters.findIndex((c) => c.id === ch.id) : 0;
  const isStreamingThis = ch && streamingChapterId === ch.id;

  // Lazy-load content when the chapter list was served without it (mobile-optimised
  // initial load strips content to keep the payload small).  Trigger only when the
  // chapter has known word-count > 0 but its content string is still empty.
  React.useEffect(() => {
    if (!ch?.id || ch.content !== '' || ch.words === 0) return;
    let cancelled = false;
    chaptersApi.get(ch.id).then((full) => {
      if (cancelled || !full?.content) return;
      setChapters((prev) =>
        prev.map((c) =>
          c.id === ch.id
            ? { ...c, content: full.content || '', words: full.words || c.words }
            : c,
        ),
      );
    }).catch(() => { /* soft fail — editor shows empty; user can type */ });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ch?.id]);

  // ------ per-panel local state ------------------------------------------
  const blankCanvasRef = useRef(null);
  const [blankHover, setBlankHover] = useState(false);
  const [suggestionsRefreshKey, setSuggestionsRefreshKey] = useState(0);
  const [inlinePopover, setInlinePopover] = useState(null);   // { suggestion, anchorEl }
  // iter-p3-v — Live AI-share for THIS chapter, so paragraph tints
  // reflect the current provenance without opening the drawer.
  const [aiShareForCh, setAiShareForCh] = useState(null);
  React.useEffect(() => {
    const pid = project?.id;
    if (!pid) { setAiShareForCh(null); return undefined; }
    let cancelled = false;
    const fetchShare = async () => {
      try {
        const { data } = await (await import('axios')).default.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/projects/${pid}/editing-record/per-chapter`,
          { withCredentials: true, timeout: 15000 },
        );
        if (cancelled) return;
        const map = data?.chapters || {};
        setAiShareForCh(map);
      } catch { if (!cancelled) setAiShareForCh(null); }
    };
    fetchShare();
    const iv = setInterval(fetchShare, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [project?.id]);

  // ------ hooks: drawers, settings, spell, suggestions, AI, mutations ----
  const drawers  = useEditorDrawers();
  const save     = useSaveState();   // iter55g17 — shared save-state pill.
  const [saveAsOpen, setSaveAsOpen] = React.useState(false);

  // loom:save-as-needed — fired by useChapterMutations when !chapter
  React.useEffect(() => {
    const onSaveAsNeeded = (e) => {
      // Forward the content to the dialog via a second event it can consume
      window.dispatchEvent(new CustomEvent('loom:save-as-pending-content', { detail: e.detail }));
      setSaveAsOpen(true);
    };
    window.addEventListener('loom:save-as-needed', onSaveAsNeeded);
    return () => window.removeEventListener('loom:save-as-needed', onSaveAsNeeded);
  }, []);

  // ── Arc Walker Auto-Trigger ───────────────────────────────────────────────
  // After each successful chapter save, silently re-score if no run has
  // happened yet, or if the last run was > 15 minutes ago. This keeps the
  // publishable score badge in ChaptersPanel current without a manual click.
  const arcAutoRef  = useRef(null);   // last auto-run timestamp (arc walker)
  const saveCountRef = useRef(0);      // saves since last novel analysis trigger
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

  // ── Continuity Audit add-on ($9.99 pay-per-use) ───────────────────────────
  const [continuityBusy, setContinuityBusy] = useState(false);
  const [continuityBuyDialog, setContinuityBuyDialog] = useState(null); // 402 detail

  const runContinuityAudit = useCallback(async () => {
    const pid = project?.id;
    if (!pid || continuityBusy) return;
    setContinuityBusy(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/projects/${pid}/continuity-review/start`, {},
        { withCredentials: true },
      );
      if (data?.started) {
        toast.success('Continuity review started — results will appear in the Review panel.');
      } else {
        toast.info(data?.note || 'Continuity review queued.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 402 && detail?.addon_sku) {
        setContinuityBuyDialog(detail);
      } else if (status === 409) {
        toast.info('A continuity review is already running for this project.');
      } else {
        toast.error('Could not start continuity review. Please try again.');
      }
    } finally {
      setContinuityBusy(false);
    }
  }, [project?.id, continuityBusy, API_BASE]);

  const handleContinuityBuy = useCallback(async () => {
    try {
      const d = await bonusAddonsApi.checkout('continuity_audit_1');
      if (d?.checkout_url) window.location.href = d.checkout_url;
      else toast.error('Could not start Stripe checkout. Please try again.');
    } catch {
      toast.error('Could not start Stripe checkout. Please try again.');
    }
  }, []);
  useEffect(() => {
    if (save.state !== 'saved' || !project?.id) return;

    // ── Arc Walker Auto-Trigger (15 min debounce) ──
    const now = Date.now();
    const MIN_INTERVAL = 15 * 60 * 1000;
    if ((!arcAutoRef.current || now - arcAutoRef.current >= MIN_INTERVAL)
        && window.localStorage.getItem('loom:arc_quiet') !== '1') {
      arcAutoRef.current = now;
      // Fire-and-forget with short timeout — backend starts background job instantly
      // and stores result on the project; arc_walker_result is picked up on next drawer open.
      axios.post(`${API_BASE}/projects/${project.id}/arc-walker`, {}, { withCredentials: true, timeout: 15000 })
        .catch(() => {});
    }

    // ── Novel Sentiment Auto-Trigger (every 5th save) ──
    saveCountRef.current += 1;
    if (saveCountRef.current % 5 === 0) {
      // POST now returns a job_id immediately (< 300 ms) and the BG worker
      // runs asynchronously — no more 180 s blocking request.
      axios.post(`${API_BASE}/analyze/novel/${project.id}`, {}, { withCredentials: true })
        .catch(() => {});  // silent — AnalysisPanel will pick up next time it loads cache
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [save.state]);

  // ── Arc Score Auto-Refresh on chapter navigation ───────────────────────────
  // When the author switches to a different chapter (ch?.id changes) and the
  // chapter has real prose, silently kick off generate-arc-scores so the Arc
  // Visualizer always reflects the latest chapter content. Rate-limited to
  // once per 5 minutes per chapter to avoid hammering the LLM endpoint.
  const arcScoreLastRef = useRef({});   // { [chapterId]: timestamp }
  useEffect(() => {
    const cid = ch?.id;
    const pid = project?.id;
    if (!cid || !pid) return;
    if (!ch?.content?.trim()) return;   // skip blank chapters
    const now = Date.now();
    const last = arcScoreLastRef.current[cid] || 0;
    if (now - last < 5 * 60 * 1000) return;   // 5-min rate limit per chapter
    arcScoreLastRef.current[cid] = now;
    axios.post(`${API_BASE}/projects/${pid}/generate-arc-scores`, {}, { withCredentials: true, timeout: 90000 })
      .catch(() => {});   // silent — ArcVisualizerPanel picks up on next open
  // ch?.id changes are the trigger; isEmpty and project.id are guards
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ch?.id]);
  const { settings, persistSettings, scrollAreaRef } = useManuscriptSettings({ project });
  const { suggestions: pendingSuggestions, liveConnected: suggestionsLive } = useChapterSuggestions({
    projectId: project?.id,
    chapterId: activeChapterId,
    refreshKey: suggestionsRefreshKey,
  });
  const { spellChecking, spellIssues, spellOpen, setSpellOpen, runSpellCheck } = useSpellCheck({
    project, chapter: ch, isDemo, recordLock,
  });

  // Keyboard shortcuts, spell drawer sync, and blank-canvas focus.
  useEditorShortcuts({ drawers, spellOpen, blankCanvasRef, chapterId: ch?.id });

  // Derived data used by the mutation + AI hooks. Both must run on
  // EVERY render (Rules of Hooks) so they live BEFORE the loading-early
  // -return below. They tolerate a null chapter — see the hook bodies.
  const paragraphs = (ch?.content && ch.content.trim())
    ? ch.content.split(/\n\n+/).map((t, i) => ({ id: `p-${ch.id}-${i}`, text: t.trim() })).filter((p) => p.text)
    : SEED_FALLBACK.map((t, i) => ({ id: `seed-${i}`, text: t }));
  const isEmpty = !ch?.content || !ch.content.trim();

  const { saveParagraphEdit, mergeParagraphUp, crossParaDelete, undoCrossParaDelete } = useChapterMutations({
    chapter: ch, paragraphs, isEmpty, setChapters, reportSave: save.track,
    projectId: project?.id,
  });
  const { busy, toneBusy, suggestion, askContinue, askToneShift, acceptSuggestion, rejectSuggestion } =
    useEditorAiActions({
      project, chapter: ch, paragraphs, isDemo, recordLock, setChapters, refreshTokens,
      reportSave: save.track,
    });

  // ------ token cost heuristics ------------------------------------------
  // Must sit above the early `!ch` return to satisfy Rules-of-Hooks.
  const {
    remaining, continueCost, draftCost, toneCost,
    enoughForContinue, enoughForDraft, enoughForTone,
  } = useTokenCostHeuristics({ project, tokens });

  // Feb-2026 · Cost Preflight — every Continue / Draft chapter click
  // goes through this so the writer sees the estimated USD cost before
  // any tokens are spent. Threshold-based auto-approve is respected
  // when the user has ticked "Don't ask again under $X" previously.
  const activeModel = React.useMemo(() => {
    const id = project?.model_id || '';
    if (id.startsWith('gpt-'))    return { id, label: 'GPT', provider: 'openai',    cost_multiplier: 1.1 };
    if (id.startsWith('gemini-')) return { id, label: 'Gemini', provider: 'gemini',  cost_multiplier: 0.35 };
    if (id.startsWith('claude-opus')) return { id, label: 'Claude Opus', provider: 'anthropic', cost_multiplier: 3.0 };
    if (id.startsWith('claude-')) return { id, label: 'Claude', provider: 'anthropic', cost_multiplier: 1.0 };
    return { id: id || 'gpt-5-2', label: 'Default', provider: 'openai', cost_multiplier: 1.1 };
  }, [project?.model_id]);

  // Rough words-out from the token estimate (~4 chars/word ≈ 0.75 word/token).
  const preflightContinue = useCostPreflight({
    model: activeModel, words: Math.round((continueCost || 0) * 0.75),
    action: 'continue this passage',
  });
  const preflightDraft = useCostPreflight({
    model: activeModel, words: Math.round((draftCost || 0) * 0.75),
    action: 'draft the full chapter',
  });

  // iter-training-v-ach-coach — Live-coach hook. Debounced auto-fires
  // as the chapter grows past every ~500 chars; also exposes a manual
  // trigger. `content` intentionally reads from the resolved chapter
  // object so a chapter switch resets the state cleanly.
  // ── Creation Journey pill state (synced via localStorage + custom event) ──
  const [journeyPill, setJourneyPill] = React.useState(readJourneyState);
  useEffect(() => {
    // Tell CreationJourneyCard that EditorPanel is rendering the minimised pill
    window.__loom_editor_journey_pill__ = true;
    // Notify CJ so it re-renders and hides its own standalone pill immediately
    window.dispatchEvent(new CustomEvent('loom:cj-state-changed'));
    const update = () => setJourneyPill(readJourneyState());
    window.addEventListener('loom:cj-state-changed', update);
    return () => {
      delete window.__loom_editor_journey_pill__;
      // Notify CJ so it re-renders and shows its standalone pill again
      window.dispatchEvent(new CustomEvent('loom:cj-state-changed'));
      window.removeEventListener('loom:cj-state-changed', update);
    };
  }, []);

  const liveCoach = useLiveCoach({
    projectId: project?.id,
    chapterId: ch?.id,
    content:   ch?.content || '',
  });
  // iter-p2 — History drawer state (coach suggestions log).
  const [coachHistoryOpen, setCoachHistoryOpen] = React.useState(false);
  // coach-beat / live-coach — single CoachHintDialog instance shared by both
  // the Beat coaching hint in the manuscript body AND the ⚡ AI Actions button
  // in the LiveCoachPopover.  `editorCoachHint` carries { label, body }.
  const [editorCoachDialog, setEditorCoachDialog] = React.useState(false);
  const [editorCoachHint, setEditorCoachHint] = React.useState(null);
  // Feb-2026 — "Generate Synopsis" dialog.
  const [synopsisOpen, setSynopsisOpen] = React.useState(false);
  const [plotBuilderOpen, setPlotBuilderOpen] = React.useState(false);
  const [rewriteOpen,     setRewriteOpen]     = React.useState(false);
  // iter-synopsis-hub — Setup menu's "Synopsis Tools" hub is a second
  // door into this SAME dialog instance (no separate state/dialog).
  // Also check for a pending intent set BEFORE this panel mounted (e.g.
  // the Home Launcher's "Generate a synopsis" card, which switches away
  // from Home into this panel in the same click — the live listener
  // below can't catch an event fired before it existed).
  React.useEffect(() => {
    if (consumePendingSynopsisOpen()) setSynopsisOpen(true);
    const onOpen = () => {
      // Clear a leftover pending flag too — otherwise it survives to
      // the next mount (e.g. leaving and re-entering the editor) and
      // re-opens the dialog on its own, kicking off an unrequested AI
      // run.
      consumePendingSynopsisOpen();
      setSynopsisOpen(true);
    };
    window.addEventListener('loom:open-manuscript-synopsis', onOpen);
    return () => window.removeEventListener('loom:open-manuscript-synopsis', onOpen);
  }, []);

  // loom:open-plot-builder — dispatched by OnboardingActivationWizard
  React.useEffect(() => {
    const onOpen = () => setPlotBuilderOpen(true);
    window.addEventListener('loom:open-plot-builder', onOpen);
    return () => window.removeEventListener('loom:open-plot-builder', onOpen);
  }, []);

  // loom:continuity-audit — dispatched by right-click context menu
  React.useEffect(() => {
    window.addEventListener('loom:continuity-audit', runContinuityAudit);
    return () => window.removeEventListener('loom:continuity-audit', runContinuityAudit);
  }, [runContinuityAudit]);

  // Feb-2026 · "Save and Sign Out" (MenuBar) needs to be sure the
  // paragraph the writer was mid-edit in actually lands before the
  // session ends — paragraphs only persist on blur, not per keystroke.
  // Blur whatever's focused inside the manuscript, then wait for the
  // shared save-state to settle before telling MenuBar it's clear to
  // log out.
  const saveStateRef = React.useRef(save.state);
  React.useEffect(() => { saveStateRef.current = save.state; }, [save.state]);
  // Feb-2026 · "Save Reminder Pulse" — broadcast a live "do I have
  // unsaved changes?" flag so MenuBar's Save & Sign Out button can
  // pulse. Paragraphs only persist on blur, so `typingDirty` covers the
  // gap between a keystroke and that blur; it's cleared the moment blur
  // fires (not only when a real save kicks off — `saveParagraphEdit`
  // no-ops on an unchanged/empty paragraph, so waiting on `save.state`
  // left the pulse stuck on forever for a no-op edit like type+undo).
  // `save.state` still covers the rest: an in-flight save, or one that
  // failed and is genuinely still unsaved.
  const [typingDirty, setTypingDirty] = useState(false);
  const hasUnsavedChanges = typingDirty || save.state === 'saving' || save.state === 'error';
  React.useEffect(() => {
    window.dispatchEvent(new CustomEvent('loom:unsaved-changes', { detail: { dirty: hasUnsavedChanges } }));
  }, [hasUnsavedChanges]);
  React.useEffect(() => () => {
    // Clear the flag on unmount (e.g. navigating away from the editor)
    // so MenuBar doesn't keep pulsing for a panel that's no longer open.
    window.dispatchEvent(new CustomEvent('loom:unsaved-changes', { detail: { dirty: false } }));
  }, []);
  React.useEffect(() => {
    const onSaveAndExit = async () => {
      const active = document.activeElement;
      if (active?.closest?.('[data-testid="manuscript-prose"]')) active.blur();
      const start = Date.now();
      await new Promise((r) => setTimeout(r, 200));   // let the blur's save kick off
      while (saveStateRef.current === 'saving' && Date.now() - start < 2500) {
        await new Promise((r) => setTimeout(r, 150));
      }
      window.dispatchEvent(new CustomEvent('loom:save-and-exit-ready', {
        detail: { ok: saveStateRef.current !== 'error' },
      }));
    };
    window.addEventListener('loom:save-and-exit', onSaveAndExit);
    return () => window.removeEventListener('loom:save-and-exit', onSaveAndExit);
  }, []);
  // Toolbar "Save Manuscript" button — same blur+wait logic as Save & Sign
  // Out, but just fires 'loom:manual-save-ready' instead of logging out.
  React.useEffect(() => {
    const onManualSave = async () => {
      const active = document.activeElement;
      if (active?.closest?.('[data-testid="manuscript-prose"]')) active.blur();
      const start = Date.now();
      await new Promise((r) => setTimeout(r, 200));
      while (saveStateRef.current === 'saving' && Date.now() - start < 2500) {
        await new Promise((r) => setTimeout(r, 150));
      }
      window.dispatchEvent(new CustomEvent('loom:manual-save-ready', {
        detail: { ok: saveStateRef.current !== 'error' },
      }));
    };
    window.addEventListener('loom:manual-save', onManualSave);
    return () => window.removeEventListener('loom:manual-save', onManualSave);
  }, []);

  // Coach Actions — highlight-insertion-point event.
  // Fired by CoachHintDialog after the "Find insertion point" action.
  // Scrolls to and briefly highlights the target paragraph in the editor.
  React.useEffect(() => {
    const onHighlight = (e) => {
      const { chapterId, afterParagraphText } = e.detail || {};
      if (chapterId && ch?.id !== chapterId) return; // wrong chapter open
      if (!afterParagraphText) return;

      const query = afterParagraphText.trim().slice(0, 60).toLowerCase();
      const prose = scrollAreaRef?.current;
      if (!prose) return;

      // Walk all paragraph contentEditable nodes
      const paras = prose.querySelectorAll('p[contenteditable]');
      let target = null;
      for (const p of paras) {
        const txt = (p.textContent || '').trim().toLowerCase();
        if (txt.startsWith(query) || txt.includes(query.slice(0, 30))) {
          target = p;
          break;
        }
      }
      // Fallback to first paragraph with text if nothing matched exactly
      if (!target) {
        for (const p of paras) {
          if ((p.textContent || '').trim().length > 20) { target = p; break; }
        }
      }
      if (!target) return;

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Flash the paragraph with a highlight ring for 3 seconds
      target.style.transition = 'background 0.2s, box-shadow 0.2s';
      target.style.background = 'rgba(99,102,241,0.12)';
      target.style.boxShadow = '0 0 0 2px rgba(99,102,241,0.45)';
      target.style.borderRadius = '3px';
      setTimeout(() => {
        target.style.background = '';
        target.style.boxShadow = '';
        target.style.borderRadius = '';
      }, 3000);
    };
    window.addEventListener('loom:highlight-insertion-point', onHighlight);
    return () => window.removeEventListener('loom:highlight-insertion-point', onHighlight);
  }, [ch?.id, scrollAreaRef]);

  // loom:open-chapter — dispatched by ChapterBibleDialog's "Find in prose"
  // feature to switch the editor to a specific chapter before highlighting.
  React.useEffect(() => {
    const onOpenChapter = (e) => {
      const { chapterId } = e.detail || {};
      if (chapterId && chapterId !== ch?.id && setActiveChapterId) {
        setActiveChapterId(chapterId);
      }
    };
    window.addEventListener('loom:open-chapter', onOpenChapter);
    return () => window.removeEventListener('loom:open-chapter', onOpenChapter);
  }, [ch?.id, setActiveChapterId]);

  // ToolsMenu → "Live Coach Suggestion" dispatches this event so the
  // menu can trigger the coach without needing the editor's internal ref.
  React.useEffect(() => {
    const onCoachTrigger = () => liveCoach.trigger?.();
    window.addEventListener('loom:coach-trigger', onCoachTrigger);
    return () => window.removeEventListener('loom:coach-trigger', onCoachTrigger);
  }, [liveCoach]);

  // ToolsMenu → "View Coach History" dispatches this event.
  React.useEffect(() => {
    const onCoachHistory = () => setCoachHistoryOpen(true);
    window.addEventListener('loom:coach-history-open', onCoachHistory);
    return () => window.removeEventListener('loom:coach-history-open', onCoachHistory);
  }, []);

  // Coach Actions — chapter content replaced event.
  // Fired by CoachHintDialog when the author accepts a rewrite.
  // Reloads the project to pull the updated chapter content.
  React.useEffect(() => {
    const onReplaced = async (e) => {
      const { chapterId } = e.detail || {};
      if (!project?.id) return;
      if (chapterId !== ch?.id) return;
      if (loadProject) await loadProject(project.id, { silent: true });
    };
    window.addEventListener('loom:chapter-content-replaced', onReplaced);
    return () => window.removeEventListener('loom:chapter-content-replaced', onReplaced);
  }, [ch?.id, project?.id, loadProject]);

  const openCoachTool = React.useCallback((hint) => {
    if (!hint) return;
    if (hint === 'suggestions')      drawers.open('suggestions');
    else if (hint === 'spellcheck')  runSpellCheck();
    else if (hint === 'story-bible' || hint === 'framework-beats' || hint === 'bible')
      drawers.open('bible');
    else if (hint === 'chapters-panel')
      /* open bookmarks as a proxy — chapters panel toggle lives in menu */
      drawers.openBookmarks('');
    liveCoach.dismiss();
  }, [drawers, runSpellCheck, liveCoach]);

  if (!ch) {
    return (
      <PanelFrame title={t('editor.loadingTitle')} icon={PenLine} onClose={onClose}>
        <div className="flex h-full items-center justify-center p-8 text-center text-muted-foreground">
          <div>
            <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" />
            <p className="mt-3 text-sm">{t('editor.loadingProject')}</p>
          </div>
        </div>
      </PanelFrame>
    );
  }

  const go = (delta) => {
    const next = chapters[Math.max(0, Math.min(chapters.length - 1, idx + delta))];
    if (next) setActiveChapterId(next.id);
  };

  // Editability is driven purely by role permission + not-streaming;
  // the SEED_FALLBACK placeholder is never persisted (see useChapterMutations).
  const paragraphsEditable = Boolean(canMutate && !isStreamingThis);

  return (
    <PanelFrame
      title={t('editor.panelTitle', { chapter: ch.title || t('editor.chapterTitle', { n: ch.number }) })}
      icon={PenLine}
      panelId="editor"
      onClose={onClose}
      actions={
        <div className="flex items-center gap-2">
          {/* Feb-2026 · Cross-post the just-finished chapter as a
           * launch/teaser. Reuses the project title + first paragraph
           * as synopsis so the caption LLM has real content to pull.
           * Hidden in Simple Mode — see MenuBar's toggle. */}
          {!simpleMode && (
            <PublishEverywhereButton
              size="sm"
              variant="outline"
              label={t('editor.crossPost')}
              testid="editor-cross-post"
              title={project?.title || ''}
              synopsis={(ch.content || '').split(/\n\n+/, 1)[0]?.slice(0, 320) || ''}
              initialMedia={project?.cover_url
                ? { url: project.cover_url, type: 'image' }
                : null}
              bookId={project?.bookstore_book_id || null}
              destUrl={project?.bookstore_book_id
                ? `${window.location.origin}/bookstore/book/${project.bookstore_book_id}`
                : null}
            />
          )}
          <EditorHeaderActions
          idx={idx}
          chapters={chapters}
          onSelectChapter={(newIdx) => {
            const target = chapters[newIdx];
            if (target?.id) setActiveChapterId(target.id);
          }}
          words={ch.words || 0}
          onOpenSearch={() => drawers.open('search')}
          onOpenBookmarks={() => drawers.openBookmarks('')}
          onToggleSuggestions={() => drawers.toggle('suggestions')}
          onToggleHistory={() => drawers.toggle('history')}
          onTogglePlagiarism={() => drawers.toggle('plagiarism')}
          onOpenBible={() => drawers.open('bible')}
          simpleMode={simpleMode}
          suggestionsOpen={drawers.suggestionsOpen}
          historyOpen={drawers.historyOpen}
          plagiarismOpen={drawers.plagiarismOpen}
          suggestionsBadge={pendingSuggestions.length}
          suggestionsLive={suggestionsLive}
          saveState={save.state}
          lastSavedAt={save.lastSavedAt}
          lastError={save.lastError}
          toneBusy={toneBusy}
          onToneShift={askToneShift}
          isDemo={isDemo}
          canGenerate={canGenerate}
          enoughForTone={enoughForTone}
          toneCost={toneCost}
          busy={busy}
          isStreaming={isStreamingThis}
          continueCost={continueCost}
          draftCost={draftCost}
          remaining={remaining}
          genTitle={genTitle}
          enoughForContinue={enoughForContinue}
          enoughForDraft={enoughForDraft}
          onContinue={() => preflightContinue.runWithPreflight(askContinue)}
          onDraft={() => {
            if (isDemo) { recordLock('Draft chapter'); return; }
            preflightDraft.runWithPreflight(() => generateChapter(ch.id));
          }}
          canBulkDraft={canBulkDraft}
          bulkDraftLockedTitle={canBulkDraft ? undefined : (upgradeCta('bulk_draft_novel')?.body || 'Draft entire novels — available on Pro Author and above.')}
          onDraftNovel={openDraftNovel}
          onGenerateSynopsis={() => setSynopsisOpen(true)}
          onOpenPlotBuilder={() => setPlotBuilderOpen(true)}
          arcScore={project?.arc_walker_result?.publishable_score ?? null}
          onSaveAs={() => setSaveAsOpen(true)}
        />
        </div>
      }
    >
      <div className="relative flex h-full flex-col">
        <DraftNovelReadinessDialog
          open={draftNovelOpen}
          onClose={closeDraftNovel}
          project={project}
          canBulkDraft={canBulkDraft}
          upgradeCta={upgradeCta}
        />
        <SaveAsDialog open={saveAsOpen} onClose={() => setSaveAsOpen(false)} />
        <SynopsisDialog
          open={synopsisOpen}
          onClose={() => setSynopsisOpen(false)}
          project={project}
        />
        <PlotBuilderDialog
          open={plotBuilderOpen}
          onClose={() => setPlotBuilderOpen(false)}
          project={project}
        />
        <RewriteDialog
          open={rewriteOpen}
          onOpenChange={setRewriteOpen}
          projectId={project?.id}
          projectTitle={project?.title}
        />

        {/* Continuity Audit — buy/confirm dialog (fires when user lacks plan cap + addon credit) */}
        {continuityBuyDialog && (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
            data-testid="continuity-buy-dialog"
          >
            <div className="mx-4 w-full max-w-sm rounded-xl border border-amber-500/30 bg-background p-6 shadow-2xl">
              <div className="mb-1 flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-amber-100 dark:bg-amber-900/40">
                  <ScanSearch className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                </span>
                <h2 className="text-[14px] font-semibold">Run a Continuity Audit</h2>
              </div>
              <p className="mb-1 text-[12.5px] text-muted-foreground leading-relaxed">
                A full continuity audit checks every chapter for character, timeline and world-building contradictions.
                This is a one-time add-on for your current plan.
              </p>
              <p className="mb-4 text-[13px] font-semibold text-amber-700 dark:text-amber-300">$9.99 · charged once via Stripe</p>
              <div className="flex gap-2">
                <button
                  onClick={handleContinuityBuy}
                  data-testid="continuity-buy-confirm-btn"
                  className="flex-1 h-9 rounded-md bg-amber-600 text-white text-[13px] font-semibold hover:bg-amber-700 transition-colors"
                >
                  Buy & Run Audit
                </button>
                <button
                  onClick={() => setContinuityBuyDialog(null)}
                  data-testid="continuity-buy-cancel-btn"
                  className="h-9 px-4 rounded-md border border-border text-[13px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="flex justify-end border-b border-panel-border px-3 py-1">
          <ChapterGradePill text={ch.content || ''} showLabel />
        </div>
        <ManuscriptToolboxDrawer
          settings={settings}
          onChange={persistSettings}
          onRunSpellCheck={runSpellCheck}
          spellChecking={spellChecking}
          onOpenRewrite={() => setRewriteOpen(true)}
          onRunContinuityAudit={runContinuityAudit}
          continuityBusy={continuityBusy}
          canContinuityAudit={canContinuityAudit}
          canScriptConvert={canScriptConvert}
        />
        <WordCountPanel canExpand={canExpandWordCount} />
        {/* Smart-recommend a cheaper model when the writer is only doing
         *  light-touch edits (Opus → Sonnet nudge). */}
        <SmartModelHint />
        {/* Token Nudge — shows at ≥80% of monthly LLM cap for non-BYOK users */}
        <TokenNudgeBanner />
        {/* AI Handled Banner — shown once per editor session */}
        <_EditorAiBanner />
        {/* AI Thinking Banner — slides in while any AI op is running */}
        <AiThinkingBanner
          busy={busy}
          toneBusy={toneBusy}
          isStreaming={isStreamingThis}
          streamingWordCount={isStreamingThis ? Math.round((streamingText?.length || 0) / 5) : 0}
        />
        {/* Streaming progress bar — thin fill under the toolbar while Claude is writing */}
        {isStreamingThis && (
          <div
            className="relative h-[2px] w-full shrink-0 overflow-hidden bg-primary/10"
            data-testid="streaming-progress-bar"
            role="progressbar"
          >
            <div
              className="absolute inset-y-0 left-0 rounded-r bg-primary transition-all duration-700 ease-out"
              style={{
                width: `${streamingText
                  ? Math.min(93, 6 + Math.round(streamingText.length / 270))
                  : 6}%`,
              }}
            />
          </div>
        )}

        <div className="relative flex flex-1 min-h-0">
          {/* Gutter — paragraph numbers next to the prose. Hidden in draft mode. */}
          <div className={`${activeMode === 'draft' ? 'hidden' : 'hidden md:flex'} w-8 shrink-0 flex-col items-end gap-[10px] border-r border-panel-border pt-12 pr-2 font-mono-stat text-[13px] text-muted-foreground/60 select-none scroll-thin overflow-hidden`}>
            {(paragraphs.length > 0 ? paragraphs : Array.from({ length: 24 })).map((_, i) => (
              <span key={`pline-${i}`}>{i + 1}</span>
            ))}
          </div>

          <ManuscriptBody
            scrollAreaRef={scrollAreaRef}
            ch={ch}
            paragraphs={paragraphs}
            paragraphsEditable={paragraphsEditable}
            isEmpty={isEmpty}
            aiShare={aiShareForCh?.[ch?.id]?.ai_share_pct ?? null}
            isStreamingThis={isStreamingThis}
            streamingText={streamingText}
            settings={settings}
            activeMode={activeMode}
            canMutate={canMutate}
            canGenerate={canGenerate}
            canAiWriteChapters={canAiWriteChapters}
            setActiveMode={setActiveMode}
            blankCanvasRef={blankCanvasRef}
            blankHover={blankHover}
            setBlankHover={setBlankHover}
            onGenerateChapter={guardedGenerateChapter}
            saveParagraphEdit={saveParagraphEdit}
            crossParaDelete={crossParaDelete}
            undoCrossParaDelete={undoCrossParaDelete}
            onDirty={() => setTypingDirty(true)}
            onDirtyClear={() => setTypingDirty(false)}
            mergeParagraphUp={mergeParagraphUp}
            pendingSuggestions={pendingSuggestions}
            onAnchorClick={(sug, ev) => {
              const anchorEl = ev?.currentTarget || ev?.target || null;
              setInlinePopover({ suggestion: sug, anchorEl });
            }}
            suggestion={suggestion}
            onAcceptSuggestion={acceptSuggestion}
            onRejectSuggestion={rejectSuggestion}
            toneBusy={toneBusy}
            isDemo={isDemo}
            askToneShift={askToneShift}
            enoughForTone={enoughForTone}
            toneCost={toneCost}
            remaining={remaining}
            onCoachActionsClick={(hint) => {
              setEditorCoachHint(hint);
              setEditorCoachDialog(true);
            }}
          />

          <EditorDrawers
            project={project}
            chapter={ch}
            user={user}
            canMutate={canMutate}
            scrollAreaRef={scrollAreaRef}
            historyOpen={drawers.historyOpen}
            plagiarismOpen={drawers.plagiarismOpen}
            spellOpen={drawers.spellOpen}
            bibleOpen={drawers.bibleOpen}
            searchOpen={drawers.searchOpen}
            bookmarksOpen={drawers.bookmarksOpen}
            suggestionsOpen={drawers.suggestionsOpen}
            setHistoryOpen={(v) => drawers.setOpen('history', v)}
            setPlagiarismOpen={(v) => drawers.setOpen('plagiarism', v)}
            setSpellOpen={(v) => { drawers.setOpen('spell', v); setSpellOpen(Boolean(v)); }}
            setBibleOpen={(v) => drawers.setOpen('bible', v)}
            setSearchOpen={(v) => drawers.setOpen('search', v)}
            setBookmarksOpen={(v) => drawers.setOpen('bookmarks', v)}
            setSuggestionsOpen={(v) => drawers.setOpen('suggestions', v)}
            spellIssues={spellIssues}
            pendingBookmarkSel={drawers.pendingBookmarkSel}
            setPendingBookmarkSel={drawers.setPendingBookmarkSel}
            suggestionsRefreshKey={suggestionsRefreshKey}
            setActiveChapterId={setActiveChapterId}
            loadProject={loadProject}
            onSuggestionCreated={() => setSuggestionsRefreshKey((k) => k + 1)}
            onSuggestionResolved={() => setSuggestionsRefreshKey((k) => k + 1)}
          />

          {/* Inline resolve popover for anchor highlights. */}
          {inlinePopover && (
            <InlineSuggestionPopover
              suggestion={inlinePopover.suggestion}
              anchorEl={inlinePopover.anchorEl}
              isOwner={Boolean(user?.user_id) && project?.user_id === user.user_id}
              currentUserId={user?.user_id}
              onClose={() => setInlinePopover(null)}
              onResolved={async () => {
                setSuggestionsRefreshKey((k) => k + 1);
                if (project?.id) await loadProject(project.id, { silent: true });
              }}
            />
          )}
          {/* iter-training-v-ach-coach — Journey pill + Beat hint + History in one
              pill row so they never overlap regardless of language/text length. */}
          <div className="pointer-events-none absolute bottom-4 left-4 z-30 flex items-center gap-2">
            {/* Journey pill — only when CreationJourneyCard is minimised */}
            {journeyPill.visible && (
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('loom:open-creation-journey'))}
                data-testid="editor-journey-pill"
                title="Open Creation Journey"
                className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11.5px] font-semibold shadow-lg transition hover:brightness-110"
                style={{ backgroundColor: '#1c1410', borderColor: 'rgba(251,191,36,0.55)', color: 'rgba(251,191,36,0.9)' }}
              >
                <Map className="h-3.5 w-3.5" />
                Journey {journeyPill.count}/{journeyPill.total}
                <ChevronUp className="h-3 w-3 opacity-70" />
              </button>
            )}
            <button
              type="button"
              onClick={liveCoach.trigger}
              disabled={liveCoach.loading}
              className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground shadow-lg transition hover:brightness-110 disabled:opacity-60"
              data-testid="live-coach-trigger"
              title={t('editor.coachTriggerTitle')}
            >
              {liveCoach.loading
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Sparkles className="h-3.5 w-3.5" />}
              {t('editor.coachSuggestNextBeat')}
            </button>
            {/* iter-p2 — Coach History quick-access. */}
            <button
              type="button"
              onClick={() => setCoachHistoryOpen(true)}
              className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground shadow-lg transition hover:brightness-110"
              data-testid="live-coach-history-btn"
              title={t('editor.coachHistoryTitle')}
            >
              <History className="h-3.5 w-3.5" />
              {t('editor.coachHistory')}
            </button>
          </div>
          <LiveCoachPopover
            suggestion={liveCoach.suggestion}
            loading={liveCoach.loading && !liveCoach.suggestion}
            onDismiss={liveCoach.dismiss}
            onOpenTool={openCoachTool}
            containerRef={scrollAreaRef}
            onActionsClick={liveCoach.suggestion ? () => {
              setEditorCoachHint({
                label: liveCoach.suggestion.title,
                body:  liveCoach.suggestion.tip,
              });
              setEditorCoachDialog(true);
            } : undefined}
          />
          <CoachHistoryDialog
            open={coachHistoryOpen}
            onOpenChange={setCoachHistoryOpen}
            projectId={project?.id}
            onOpenTool={openCoachTool}
          />
          {/* coach-beat / live-coach — shared AI Actions dialog for both the
              manuscript Beat hint and the LiveCoachPopover ⚡ button. */}
          <CoachHintDialog
            open={editorCoachDialog}
            onClose={() => { setEditorCoachDialog(false); setEditorCoachHint(null); }}
            chapterId={ch?.id}
            chapterNumber={ch?.number}
            chapterTitle={ch?.title}
            hintLabel={editorCoachHint?.label}
            hintBody={editorCoachHint?.body}
            setChapters={setChapters}
          />
        </div>
      </div>
      {/* Feb-2026 · Cost preflight dialogs — hidden until a Continue /
          Draft click asks for approval. */}
      <preflightContinue.PreflightDialog />
      <preflightDraft.PreflightDialog />
    </PanelFrame>
  );
}
